
import { defineComponent } from 'vue';
import { mapStores } from 'pinia';
// Components
import AuthView from 'client/containers/user/Views/AuthView.vue';
import DuoUserResetPasswordRedirectHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/DuoUserResetPasswordRedirectHandler.vue';
import LogoIcon from 'client/components/LogoIcon.vue';
import MfaView from 'client/containers/user/Views/MfaView.vue';
import OtpUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/OtpUserResetPasswordHandler.vue';
import PushUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/PushUserResetPasswordHandler.vue';
import TotpUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/TotpUserResetPasswordHandler.vue';
import UserResetPasswordInputHandler from 'client/containers/user/UserResetPassword/UserResetPasswordInputHandler.vue';
import WebAuthnUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/WebAuthnUserResetPasswordHandler.vue';

// Constants
import { LoginConsts } from 'client/util/Constants';
import {
  SMS_OTP_IDLE_TIMEOUT_STATUS_CODE,
  SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE,
  createSmsOtpLoginSession,
} from 'client/util/Constants/smsOtpLoginSession';
import MfaConstants from 'client/containers/Login/MfaConstants';
import type { MfaFactor } from 'client/types/Auth';
import type { SmsOtpLoginSessionPatch } from 'client/util/Constants/smsOtpLoginSession';

// Service Errors
import MfaRequiredError from 'client/services/response/MfaRequiredError';
import RequestError from 'client/services/response/RequestError';

// Utils
import {
  clearPwdResetSessionEmail,
  syncPwdResetSessionEmail,
} from 'client/util/pwdResetSessionEmail';
import { useMfaLabels } from 'client/util/Constants/MfaLabels';
import ProcessPasswordResetMfaError from 'client/containers/user/MfaComponents/mfaUtils/processPasswordResetMfaError';
import UserLoginStore from 'client/stores/UserLoginStore';
import getCurrentComponentNameFromList from 'client/containers/user/MfaComponents/mfaUtils/getCurrentComponentNameFromList';
import getLastUsedFactor from 'client/containers/user/MfaComponents/mfaUtils/getLastUsedFactor';
import mfaFactorBuilder from 'client/containers/user/MfaComponents/mfaUtils/mfaFactorBuilder';
import routerService from 'client/services/routerService';
import userResetPasswordService from 'client/services/userResetPasswordService';

interface pwdResetForm {
  password: string;
  confirm: string;
  challenge?: any;
  pwdResetToken?: any;
}

const MFA_DIRECT_TEMPLATE = 'resetPasswordUserMfaDirect';
const EMAIL_RESET_FORM_TEMPLATE = 'resetPasswordUserForm';
const REASON_ALLOW_UNENROLLED = 'ALLOW_UNENROLLED';
const REASON_FEATURE_DISABLED = 'FEATURE_DISABLED';

const { mfaNames } = MfaConstants;
const {
  responseMessages: {
    mfaUnavailableForPwdReset,
    passwordResetError,
    passwordResetSuccess,
  },
} = LoginConsts;

const componentsUnderControl = [
  {
    id: '',
    componentName: UserResetPasswordInputHandler.name,
  },
  {
    id: mfaNames.duo,
    componentName: DuoUserResetPasswordRedirectHandler.name,
  },
  {
    id: mfaNames.sms_otp,
    componentName: OtpUserResetPasswordHandler.name,
  },
  {
    id: mfaNames.push,
    componentName: PushUserResetPasswordHandler.name,
  },
  {
    id: mfaNames.totp,
    componentName: TotpUserResetPasswordHandler.name,
  },
  {
    id: mfaNames.webauthn,
    componentName: WebAuthnUserResetPasswordHandler.name,
  },
];

const getCurrentComponentName = getCurrentComponentNameFromList(componentsUnderControl);

const formInitialState: pwdResetForm = {
  password: '',
  confirm: '',
};

export default defineComponent({
  name: 'UserResetPasswordController',

  components: {
    AuthView,
    DuoUserResetPasswordRedirectHandler,
    LogoIcon,
    MfaView,
    OtpUserResetPasswordHandler,
    PushUserResetPasswordHandler,
    TotpUserResetPasswordHandler,
    UserResetPasswordInputHandler,
    WebAuthnUserResetPasswordHandler,
  },

  props: {
    context: {
      type: String,
      default: undefined,
    },
    mfaType: {
      type: String,
      default: '',
    },
    redirectTo: {
      type: String,
      default: undefined,
    },
    requestKey: {
      type: String,
      default: '',
    },
    step: {
      type: String,
      default: '',
    },
  },

  setup() {
    const mfaLabels = useMfaLabels();

    return {
      mfaLabels,
    };
  },

  data() {
    return {
      // False for MFA-direct until SI eligibility resolves (set in created)
      allowRender: true,
      errorMessage: '',
      // using undefined so we display 0
      errorMessageDisplayDuration: undefined as number | undefined,
      factors: [] as MfaFactor[],
      form: {
        ...formInitialState,
      },
      smsOtpLoginSession: createSmsOtpLoginSession(),
    };
  },

  computed: {
    ...mapStores(UserLoginStore),

    componentHandlers() {
      return {
        resetErrorMessage: this.onResetErrorMessage,
        success: this.onSuccess,
        'sms-session-patch': this.patchSmsOtpLoginSession,
        'mfa-direct-ready': this.onMfaDirectPasswordReady,
      };
    },

    componentName() {
      const id = this.isMfa ? this.mfaType : this.step;

      return getCurrentComponentName(id);
    },

    /**
     * MFA-direct commit uses pwdResetToken as :key (ADR-002).
     * Password step must stay keyless so InputHandler always re-inits after restart.
     * Email-link path keeps the activation/recovery key from the route.
     */
    effectiveRequestKey(): string {
      if (this.isMfaDirect) {
        if (!this.isMfa) {
          return '';
        }

        return this.form.pwdResetToken
          || this.userLoginStore.pwdResetToken
          || '';
      }

      return this.requestKey;
    },

    componentProps() {
      const base = {
        form: this.form,
        hasPendingErrorMessage: this.hasPendingErrorMessage,
        isMfaDirect: this.isMfaDirect,
        requestKey: this.effectiveRequestKey,
      };

      if (this.isMfa && this.mfaType === mfaNames.sms_otp) {
        return {
          ...base,
          smsLoginSession: this.smsOtpLoginSession,
        };
      }

      return base;
    },

    mfaFactorsForView(): MfaFactor[] {
      if (!this.smsOtpLoginSession.removedFromChooser) {
        return this.factors;
      }

      return this.factors.filter((f) => f.name !== mfaNames.sms_otp);
    },

    /**
     * MFA-direct mode is determined by the route template only.
     * Do not trust store.resetPath alone — a prior MFA method choice can leave
     * resetPath === 'mfa-direct' and would mis-handle email activation-link reset
     * (resetPasswordUserForm + key) in the same session.
     */
    isMfaDirect(): boolean {
      return this.$route?.query?.template === MFA_DIRECT_TEMPLATE;
    },

    ssoQueryParams(): Record<string, string> {
      return this.context && this.redirectTo
        ? { context: this.context, redirectTo: this.redirectTo }
        : {};
    },

    controllingQueryParams() {
      if (this.isMfaDirect) {
        return {
          template: MFA_DIRECT_TEMPLATE,
          ...this.ssoQueryParams,
        };
      }

      return {
        template: EMAIL_RESET_FORM_TEMPLATE,
        key: this.requestKey,
        ...this.ssoQueryParams,
      };
    },

    hasPendingErrorMessage() {
      const remainingDuration = this.errorMessageDisplayDuration || -1;
      return remainingDuration >= 0;
    },

    headerText() {
      return this.isMfa ? this.mfaLabels.verifyIdentity : this.mfaLabels.passwordResetHeader;
    },

    isMfa() {
      return this.step === 'mfa';
    },
  },

  created() {
    // Clear stale MFA-direct marker when landing on the email activation-link flow
    if (this.$route?.query?.template === EMAIL_RESET_FORM_TEMPLATE
      && this.userLoginStore.resetPath === 'mfa-direct') {
      this.userLoginStore.resetPath = 'email';
    }

    if (!this.isMfaDirect) {
      return;
    }

    // Hold paint until SI eligibility (same FEATURE_DISABLED / ALLOW_UNENROLLED gate as chooser)
    this.allowRender = false;
    this.userLoginStore.resetPath = 'mfa-direct';
  },

  async mounted() {
    if (this.isMfaDirect) {
      await this.resolveMfaDirectAccess();
      if (!this.allowRender) {
        return;
      }
    }

    this.validateInitialStep();
  },

  methods: {
    /**
     * Deep-link / bookmark kill-switch: probe SI like forgot-password chooser.
     * FEATURE_DISABLED / ALLOW_UNENROLLED / probe failure → email reset request (email known).
     * Missing email after session restore → login (NA-4597: never auto-choose email reset).
     * Other ineligible reasons (suspended, unknown, NO_FACTORS, …) keep the form — anti-enum /
     * same UX as selecting MFA from the chooser; SI still rejects on mfa-init.
     */
    async resolveMfaDirectAccess() {
      const email = syncPwdResetSessionEmail(
        () => this.userLoginStore.email || '',
        (value) => { this.userLoginStore.email = value; },
      );
      if (!email) {
        // Cold deep-link / cleared session — restart at login, not email-reset confirmation
        routerService.goToUserLogin({ query: this.ssoQueryParams });
        return;
      }

      try {
        const result = await userResetPasswordService.checkMfaEligibility({ email });
        this.userLoginStore.eligibility = result;
        if (result.reason === REASON_FEATURE_DISABLED
          || result.reason === REASON_ALLOW_UNENROLLED) {
          // Keep session email so email-reset request still has identity after navigation
          routerService.goToUserPasswordResetRequest({ query: this.ssoQueryParams });
          return;
        }
        this.allowRender = true;
      } catch {
        this.userLoginStore.eligibility = { eligible: false };
        routerService.goToUserPasswordResetRequest({ query: this.ssoQueryParams });
      }
    },

    clearMfaDirectEmailSession() {
      clearPwdResetSessionEmail();
    },

    handleErrorCountdown() {
      const errorCountdown = setInterval(() => {
        if ((this.errorMessageDisplayDuration as number) < 1) {
          this.onResetErrorMessage();
          clearInterval(errorCountdown);
        }

        (this.errorMessageDisplayDuration as number) -= 1;
      }, 1000);
    },

    handleResetForm() {
      this.form = {
        ...formInitialState,
      };
      // Clear stale MFA-direct session so restart re-runs initMfaReset
      this.userLoginStore.pwdResetToken = '';
    },

    /**
     * MFA-direct password submit (NA-4402):
     * 1) initMfaReset → mint pwdResetToken
     * 2) probe POST /changepassword/:pwdResetToken → MFA factors + challenge
     * 3) onCredentialError → getLastUsedFactor auto-initiation
     *
     * `done` keeps InputHandler loading until init/probe finish.
     */
    async onMfaDirectPasswordReady(payload: { done?: () => void } = {}) {
      const { done } = payload;
      try {
        // Defense-in-depth: never mint MFA-direct session on email activation route
        if (!this.isMfaDirect) {
          return;
        }

        const { email } = this.userLoginStore;
        if (!email) {
          this.setErrorMessage(passwordResetError);
          return;
        }

        const { pwdResetToken } = await userResetPasswordService.initMfaReset({ email });
        this.form.pwdResetToken = pwdResetToken;
        this.userLoginStore.pwdResetToken = pwdResetToken;

        // Probe commit (no MFA proof yet) to obtain factors + Duo/WebAuthn challenge
        await userResetPasswordService.resetPassword(pwdResetToken, this.form);
        // Session requires MFA; success here is unexpected but treat as complete
        this.onSuccess();
      } catch (error: unknown) {
        this.onCredentialError(error);
      } finally {
        done?.();
      }
    },

    onCredentialError(error: unknown) {
      // todo: update error message to respond to action
      if (error instanceof RequestError) {
        return this.setErrorMessage(error.message);
      }
      if (!(error instanceof MfaRequiredError)) {
        return this.setErrorMessage(passwordResetError);
      }

      this.smsOtpLoginSession = createSmsOtpLoginSession();

      this.factors = mfaFactorBuilder(
        {
          factors: error.factors,
        },
        {
          controllingQueryParams: this.controllingQueryParams,
        },
      );

      if (this.factors.length === 0) {
        return this.setErrorMessage(mfaUnavailableForPwdReset);
      }

      this.form.challenge = error.challenge;
      this.form.pwdResetToken = error.pwdResetToken || this.form.pwdResetToken;
      if (this.form.pwdResetToken) {
        this.userLoginStore.pwdResetToken = this.form.pwdResetToken;
      }

      const mfaParam = getLastUsedFactor(this.factors);

      return routerService.goToUserLogin({
        query: {
          ...this.controllingQueryParams,
          step: 'mfa',
          ...mfaParam,
        },
      });
    },

    patchSmsOtpLoginSession(patch: SmsOtpLoginSessionPatch) {
      const s = this.smsOtpLoginSession;

      if (patch.initialSendEverAttempted !== undefined) {
        s.initialSendEverAttempted = patch.initialSendEverAttempted;
      }

      if (patch.successfulSendIncrement) {
        s.successfulSendCount += patch.successfulSendIncrement;
      }

      if (patch.lastSessionId !== undefined) {
        s.lastSessionId = patch.lastSessionId;
      }

      if (patch.removedFromChooser !== undefined) {
        s.removedFromChooser = patch.removedFromChooser;
      }
    },

    onMfaError(error: unknown) {
      if (error instanceof RequestError) {
        const smsOtpEndsSession = error.statusCode === SMS_OTP_IDLE_TIMEOUT_STATUS_CODE
          || error.statusCode === SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE;

        if (smsOtpEndsSession) {
          this.smsOtpLoginSession.removedFromChooser = true;
        }
      }

      const {
        message,
        action,
        displayDuration,
      } = ProcessPasswordResetMfaError(this.mfaType, error);
      this.errorMessageDisplayDuration = displayDuration;

      if (this.errorMessageDisplayDuration && this.errorMessageDisplayDuration > 0) {
        this.handleErrorCountdown();
      }

      if (action === 'exitToLogin') {
        this.handleResetForm();
        this.redirectToLogin(message);
      } else if (action === 'restart') {
        this.handleResetForm();
        this.redirectBackToResetStart(message);
      } else {
        this.setErrorMessage(message);
      }
    },

    onResetErrorMessage() {
      this.errorMessageDisplayDuration = undefined;
      this.setErrorMessage();
    },

    onSuccess() {
      this.clearMfaDirectEmailSession();
      routerService.goToUserLogin({ params: { success: passwordResetSuccess } });
    },

    redirectBackToResetStart(message?: string) {
      if (message) {
        this.setErrorMessage(message);
      }

      if (this.isMfaDirect) {
        routerService.goToUserMfaDirectPasswordReset({
          query: this.ssoQueryParams,
        });
        return;
      }

      routerService.goToUserPasswordResetForm({
        query: {
          key: this.requestKey,
        },
      });
    },

    redirectToLogin(errorMessage = '') {
      this.clearMfaDirectEmailSession();
      routerService.goToUserLogin({
        params: { error: errorMessage },
        query: this.ssoQueryParams,
      });
    },

    setErrorMessage(message = '') {
      this.errorMessage = message;
    },

    validateInitialStep() {
      const isInitialStepInvalid = this.step && !this.factors.length;

      if (isInitialStepInvalid) {
        routerService.replaceToUserLogin({ query: { ...this.controllingQueryParams } });
      }
    },
  },
});
